import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./apiClient";
import { isAppAdmin } from "./permissions";

export interface CurrentUser {
  sub?: string;
  name?: string;
  displayName?: string;
  username?: string;
  login?: string;
  email?: string;
  upn?: string;
  groups?: string[];
  groups_real?: string[] | null;
  dev_role_switch?: boolean;
  dev_switcher?: boolean;
  dev_role_switch_preset?: string | null;
  [key: string]: unknown;
}

export function getUserDisplayName(user: CurrentUser | null): string {
  if (!user) return "";
  return (
    (user.displayName as string | undefined) ||
    (user.name as string | undefined) ||
    (user.email as string | undefined) ||
    (user.upn as string | undefined) ||
    ""
  );
}

/** Admin-Check auf echten Gruppen – unabhängig von der simulierten Rolle. */
export function userIsRealAppAdmin(user: CurrentUser | null): boolean {
  if (!user) return false;
  const real = user.groups_real;
  if (Array.isArray(real) && real.length > 0) {
    return isAppAdmin(real);
  }
  return isAppAdmin(user.groups || []);
}

export function getUserLocalLogin(user: CurrentUser | null): string {
  if (!user) return "";
  const login = String(user.login || "").trim();
  if (login) return login;
  const username = String(user.username || user.sub || "").trim();
  if (!username) return "";
  return username.includes("@") ? (username.split("@")[0] ?? username) : username;
}

export async function logout(): Promise<void> {
  try {
    await api.post("/auth/logout");
  } catch (err) {
    console.warn("[auth] Logout fehlgeschlagen:", err);
  } finally {
    window.location.replace("/login?logout=1");
  }
}

interface UseUserState {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
}

export function useCurrentUser(): UseUserState & { reload: () => void } {
  const [state, setState] = useState<UseUserState>({
    user: null,
    loading: true,
    error: null,
  });

  const reload = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    api
      .get<CurrentUser>("/me")
      .then((user) => setState({ user, loading: false, error: null }))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          return;
        }
        const message = err instanceof Error ? err.message : "Unbekannter Fehler";
        setState({ user: null, loading: false, error: message });
      });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { ...state, reload };
}
